import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, FolderPlus, Folder, ChevronRight, Home } from 'lucide-react';
import { api, type Asset } from '../lib/api';
import { AssetUploader } from '../components/AssetUploader';
import { BulkAssetUploader } from '../components/BulkAssetUploader';
import { AssetGrid } from '../components/AssetGrid';
import { Card } from '../components/Card';
import { PageSpinner } from '../components/Spinner';
import { cn } from '../lib/cn';
import { SETTLE, MORPH, settleDelay } from '../lib/motion';

export function AssetManager() {
  const qc = useQueryClient();
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.me });
  const [activeTab, setActiveTab] = useState<'platform' | 'orgs'>('platform');
  const [uploadMode, setUploadMode] = useState<'single' | 'bulk'>('single');
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [capInput, setCapInput] = useState('');

  // Folder navigation
  const [currentPath, setCurrentPath] = useState<string[]>([]);
  const [editingAssetId, setEditingAssetId] = useState<string | null>(null);
  const [editGroup, setEditGroup] = useState('');

  // Allocate modal
  const [allocatingAssetId, setAllocatingAssetId] = useState<string | null>(null);
  const [allocTargetOrg, setAllocTargetOrg] = useState('');

  const { data: platformAssets, isLoading: platformLoading } = useQuery({
    queryKey: ['assets', 'platform'],
    queryFn: () => api.listAssets('platform'),
  });

  const { data: orgAssets, isLoading: orgLoading } = useQuery({
    queryKey: ['assets', 'org', selectedOrgId],
    queryFn: () => api.listAssets('org', selectedOrgId),
    enabled: !!selectedOrgId,
  });

  const { data: orgConfig } = useQuery({
    queryKey: ['assetConfig', selectedOrgId],
    queryFn: () => api.getOrgAssetConfig(selectedOrgId),
    enabled: !!selectedOrgId,
  });

  const { data: orgs } = useQuery({
    queryKey: ['admin', 'orgs'],
    queryFn: () => api.orgs(),
  });

  const deleteMut = useMutation({
    mutationFn: (assetId: string) => api.deleteAsset(assetId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assets'] });
      qc.invalidateQueries({ queryKey: ['assetConfig'] });
    },
  });

  const allocMut = useMutation({
    mutationFn: () => {
      if (!allocatingAssetId || !allocTargetOrg) throw new Error('Missing target');
      return api.allocateAsset(allocatingAssetId, 'org', allocTargetOrg);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assets'] });
      setAllocatingAssetId(null);
      setAllocTargetOrg('');
    },
  });

  const updateAssetMut = useMutation({
    mutationFn: ({ assetId, group }: { assetId: string; group: string }) =>
      api.updateAsset(assetId, { group: group || '' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assets'] });
      setEditingAssetId(null);
    },
  });

  const capMut = useMutation({
    mutationFn: () => api.updateOrgAssetConfig(selectedOrgId, parseInt(capInput, 10) * 1024 * 1024),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['assetConfig'] }),
  });

  // Derive folder structure from platform assets
  const { allGroups, subfolders, currentAssets } = useMemo(() => {
    const assets = platformAssets?.assets || [];
    const groupSet = new Set<string>();

    for (const asset of assets) {
      const g = (asset as any).group as string | undefined;
      if (g) groupSet.add(g);
    }
    const allGroups = Array.from(groupSet).sort();

    // Current path as string
    const pathStr = currentPath.join('/');

    // Find immediate subfolders at current level
    const subfoldersSet = new Set<string>();
    const currentLevelAssets: Asset[] = [];

    for (const asset of assets) {
      const g = ((asset as any).group as string) || '';

      if (pathStr === '') {
        // At root
        if (!g) {
          currentLevelAssets.push(asset);
        } else {
          const topFolder = g.split('/')[0];
          subfoldersSet.add(topFolder);
        }
      } else {
        if (g === pathStr) {
          // Exact match — asset lives in this folder
          currentLevelAssets.push(asset);
        } else if (g.startsWith(pathStr + '/')) {
          // Nested deeper — extract next subfolder
          const remainder = g.slice(pathStr.length + 1);
          const nextFolder = remainder.split('/')[0];
          subfoldersSet.add(nextFolder);
        }
        // g doesn't start with pathStr — skip
      }
    }

    // Count assets and grab a preview per subfolder
    const subfolders = Array.from(subfoldersSet).sort().map(name => {
      const folderPath = pathStr ? `${pathStr}/${name}` : name;
      const folderAssets = assets.filter(a => {
        const g = ((a as any).group as string) || '';
        return g === folderPath || g.startsWith(folderPath + '/');
      });
      return { name, count: folderAssets.length, previewUrl: folderAssets[0]?.url || null };
    });

    return { allGroups, subfolders, currentAssets: currentLevelAssets };
  }, [platformAssets, currentPath]);

  if (me?.role !== 'founder') {
    return <div className="p-8 text-center text-iron-500">Founders only.</div>;
  }

  const invalidate = () => qc.invalidateQueries({ queryKey: ['assets'] });

  const assignGroup = (assetId: string) => {
    setEditingAssetId(assetId);
    const asset = (platformAssets?.assets || []).find(a => a.assetId === assetId);
    setEditGroup((asset as any)?.group || '');
  };

  return (
    <div className="mx-auto max-w-6xl space-y-8">
      <div className={SETTLE} style={settleDelay(0)}>
        <h1 className="font-display text-2xl font-800 uppercase tracking-wide text-white">Asset Manager</h1>
        <p className="mt-1 text-sm text-iron-500">Manage platform assets, org branding, and storage quotas.</p>
      </div>

      {/* Tab switcher */}
      <div className={cn('flex gap-1 rounded-lg bg-white/[0.04] p-1', SETTLE)} style={settleDelay(1)}>
        {(['platform', 'orgs'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={cn('flex-1 rounded-md px-4 py-2 font-display text-xs font-800 uppercase tracking-wider transition-colors',
              activeTab === tab ? 'bg-white/[0.07] text-accent-500' : 'text-iron-500 hover:text-iron-300'
            )}
          >
            {tab === 'platform' ? 'Platform Pool' : 'Org Assets'}
          </button>
        ))}
      </div>

      {/* ═══ PLATFORM POOL ═══ */}
      {activeTab === 'platform' && (
        <div className="space-y-6">
          {/* Upload mode toggle */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <p className="font-display text-[10px] font-800 uppercase tracking-[0.18em] text-iron-500">Upload to platform pool</p>
              <div className="flex gap-1 rounded-md bg-white/[0.04] p-0.5">
                {(['single', 'bulk'] as const).map(mode => (
                  <button
                    key={mode}
                    onClick={() => setUploadMode(mode)}
                    className={cn('rounded px-3 py-1 font-display text-[10px] font-800 uppercase tracking-wider transition-colors',
                      uploadMode === mode ? 'bg-white/[0.07] text-accent-500' : 'text-iron-500 hover:text-iron-300'
                    )}
                  >
                    {mode}
                  </button>
                ))}
              </div>
            </div>
            {uploadMode === 'single' ? (
              <AssetUploader
                ownerType="platform"
                ownerId="pool"
                folders={allGroups}
                currentFolder={currentPath.length > 0 ? currentPath.join('/') : undefined}
                onUploaded={invalidate}
              />
            ) : (
              <BulkAssetUploader ownerType="platform" ownerId="pool" existingGroups={allGroups} onComplete={invalidate} />
            )}
          </Card>

          {/* Breadcrumb navigation */}
          <div className="flex items-center gap-1.5 text-sm">
            <button
              onClick={() => setCurrentPath([])}
              className={cn('flex items-center gap-1 rounded-md px-2 py-1 transition-colors',
                currentPath.length === 0 ? 'text-accent-500' : 'text-iron-500 hover:text-iron-300'
              )}
            >
              <Home className="h-3.5 w-3.5" />
              <span className="font-display text-xs font-800 uppercase tracking-wider">Root</span>
            </button>
            {currentPath.map((segment, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <ChevronRight className="h-3 w-3 text-iron-500" />
                <button
                  onClick={() => setCurrentPath(currentPath.slice(0, i + 1))}
                  className={cn('rounded-md px-2 py-1 font-display text-xs font-800 uppercase tracking-wider transition-colors',
                    i === currentPath.length - 1 ? 'text-accent-500' : 'text-iron-500 hover:text-iron-300'
                  )}
                >
                  {segment}
                </button>
              </div>
            ))}
          </div>

          {/* Folder grid + asset grid */}
          <Card>
            {platformLoading ? <PageSpinner /> : (
              <>
                {/* Subfolders */}
                {subfolders.length > 0 && (
                  <div className="mb-6">
                    <h2 className="mb-3 font-display text-[10px] font-800 uppercase tracking-[0.18em] text-iron-500">Folders</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                      {subfolders.map(folder => (
                        <button
                          key={folder.name}
                          onClick={() => setCurrentPath(prev => [...prev, folder.name])}
                          className={cn('group relative rounded-xl bg-white/[0.03] overflow-hidden transition-colors hover:bg-white/[0.06]', MORPH)}
                        >
                          {/* Thumbnail preview or folder icon */}
                          <div className="aspect-square bg-white/[0.02] flex items-center justify-center relative">
                            {folder.previewUrl ? (
                              <>
                                <img src={folder.previewUrl} alt="" className="h-full w-full object-contain p-3 opacity-40 group-hover:opacity-60 transition-opacity" loading="lazy" />
                                <Folder className="absolute h-8 w-8 text-iron-400" />
                              </>
                            ) : (
                              <Folder className="h-12 w-12 text-iron-500 group-hover:text-iron-400 transition-colors" />
                            )}
                          </div>
                          <div className="p-2.5 text-center">
                            <p className="font-display text-xs font-800 uppercase tracking-wider text-iron-400 group-hover:text-white truncate">
                              {folder.name}
                            </p>
                            <p className="text-[10px] text-iron-500 mt-0.5">{folder.count} asset{folder.count !== 1 ? 's' : ''}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Assets in current folder */}
                {currentAssets.length > 0 && (
                  <div>
                    {subfolders.length > 0 && (
                      <h2 className="mb-3 font-display text-[10px] font-800 uppercase tracking-[0.18em] text-iron-500">
                        {currentPath.length === 0 ? 'Ungrouped Assets' : 'Assets'}
                      </h2>
                    )}
                    <AssetGrid
                      assets={currentAssets}
                      onDelete={id => deleteMut.mutate(id)}
                      onAllocate={id => setAllocatingAssetId(id)}
                      onSelect={asset => assignGroup(asset.assetId)}
                      selectable
                      selectedId={editingAssetId || undefined}
                      showOwner
                    />
                  </div>
                )}

                {subfolders.length === 0 && currentAssets.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Folder className="h-12 w-12 text-iron-700 mb-3" />
                    <p className="text-sm text-iron-500">This folder is empty</p>
                    <p className="text-xs text-iron-500 mt-1">Upload assets or drag a folder above</p>
                  </div>
                )}
              </>
            )}
          </Card>
        </div>
      )}

      {/* ═══ ORG ASSETS ═══ */}
      {activeTab === 'orgs' && (
        <div className="space-y-6">
          <Card>
            <label className="mb-2 block font-display text-[10px] font-800 uppercase tracking-[0.18em] text-iron-500">
              Select Organisation
            </label>
            <select value={selectedOrgId} onChange={e => setSelectedOrgId(e.target.value)} className="input w-full rounded-lg">
              <option value="">Choose an org...</option>
              {(orgs || []).map(org => (
                <option key={org.id} value={org.id}>{org.name || org.id}</option>
              ))}
            </select>
          </Card>

          {selectedOrgId && (
            <>
              {orgConfig && (
                <Card>
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="font-display text-sm font-800 uppercase tracking-wider text-iron-400">Storage</h2>
                    <span className="text-xs text-iron-500">
                      {(orgConfig.storageUsed / 1024 / 1024).toFixed(1)}MB / {(orgConfig.storageCap / 1024 / 1024).toFixed(0)}MB
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-white/[0.05] overflow-hidden">
                    <div className="h-full rounded-full bg-accent-500 transition-all"
                      style={{ width: `${Math.min(100, (orgConfig.storageUsed / orgConfig.storageCap) * 100)}%` }} />
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <input type="number" placeholder="Cap (MB)" value={capInput} onChange={e => setCapInput(e.target.value)} className="input w-24 rounded-lg text-sm" />
                    <button onClick={() => capMut.mutate()} disabled={!capInput || capMut.isPending}
                      className={cn('ember rounded-lg px-3 py-1.5 font-display text-[10px] font-800 uppercase tracking-wider disabled:opacity-50', MORPH)}>
                      Set Cap
                    </button>
                  </div>
                </Card>
              )}
              <Card>
                <h2 className="mb-4 font-display text-sm font-800 uppercase tracking-wider text-iron-400">Org Assets</h2>
                {orgLoading ? <PageSpinner /> : (
                  <AssetGrid assets={orgAssets?.assets || []} onDelete={id => deleteMut.mutate(id)} showOwner />
                )}
              </Card>
            </>
          )}
        </div>
      )}

      {/* ═══ ALLOCATE MODAL ═══ */}
      {allocatingAssetId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="relative w-full max-w-md rounded-lg bg-paper-panel p-6 shadow-[0_12px_32px_-6px_rgba(0,0,0,0.6)]">
            <button onClick={() => { setAllocatingAssetId(null); setAllocTargetOrg(''); }}
              className="absolute top-4 right-4 text-iron-500 hover:text-iron-300">
              <X className="h-5 w-5" />
            </button>
            <h3 className="font-display text-lg font-800 uppercase tracking-wide text-white mb-1">Assign to Organisation</h3>
            <p className="text-sm text-iron-500 mb-6">Makes this platform asset available in the selected org.</p>
            <select value={allocTargetOrg} onChange={e => setAllocTargetOrg(e.target.value)} className="input w-full rounded-lg mb-4">
              <option value="">Choose an org...</option>
              {(orgs || []).map(org => (
                <option key={org.id} value={org.id}>{org.name || org.id}</option>
              ))}
            </select>
            {allocMut.isError && <p className="mb-3 text-xs text-red-400">{(allocMut.error as Error).message}</p>}
            <div className="flex justify-end gap-3">
              <button onClick={() => { setAllocatingAssetId(null); setAllocTargetOrg(''); }} className="btn-ghost rounded-lg px-4 py-2 text-xs">Cancel</button>
              <button onClick={() => allocMut.mutate()} disabled={!allocTargetOrg || allocMut.isPending}
                className={cn('ember rounded-lg px-4 py-2 font-display text-xs font-800 uppercase tracking-wider disabled:opacity-50', MORPH)}>
                {allocMut.isPending ? 'Assigning...' : 'Assign'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ GROUP EDIT MODAL ═══ */}
      {editingAssetId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="relative w-full max-w-sm rounded-lg bg-paper-panel p-6 shadow-[0_12px_32px_-6px_rgba(0,0,0,0.6)]">
            <button onClick={() => setEditingAssetId(null)} className="absolute top-4 right-4 text-iron-500 hover:text-iron-300">
              <X className="h-5 w-5" />
            </button>
            <h3 className="font-display text-lg font-800 uppercase tracking-wide text-white mb-1">
              <FolderPlus className="inline h-4 w-4 mr-2 text-accent-500" />
              Set Group
            </h3>
            <p className="text-sm text-iron-500 mb-4">Assign this asset to a group for organisation.</p>

            <input
              type="text"
              value={editGroup}
              onChange={e => setEditGroup(e.target.value)}
              placeholder="Group name (e.g. Clan Logos, Banners)"
              className="input w-full rounded-lg mb-2"
              list="existing-groups"
            />
            <datalist id="existing-groups">
              {allGroups.map(g => <option key={g} value={g} />)}
            </datalist>
            <p className="text-[10px] text-iron-500 mb-4">Leave empty to ungroup. Type a new name to create a group.</p>

            <div className="flex justify-end gap-3">
              <button onClick={() => setEditingAssetId(null)} className="btn-ghost rounded-lg px-4 py-2 text-xs">Cancel</button>
              <button
                onClick={() => updateAssetMut.mutate({ assetId: editingAssetId, group: editGroup })}
                disabled={updateAssetMut.isPending}
                className={cn('ember rounded-lg px-4 py-2 font-display text-xs font-800 uppercase tracking-wider disabled:opacity-50', MORPH)}
              >
                {updateAssetMut.isPending ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
